import { NotImplementedError } from "../../../../errors";
import { parseDateFilter } from "../../../../models/looker/modules/overview";
import { PromiseHandler } from "../../../../handlers";
import { OVERVIEW_DAILY_IMPRESSIONS_QUERY } from "./query";
/**
 * Impressions per day, split by product group, for the Overview dashboard's
 * Daily Impressions widget. Impressions only: the widget reports delivery over
 * time and does not follow the section's metric control, so there is no metric
 * to parameterize.
 */
export default class LookerOverviewDailyImpressions extends PromiseHandler {
    constructor(sdk) {
        super(sdk, "looker-overview-daily-impressions");
    }
    async findItems(filters) {
        const dateFilter = filters.where?.find((filter) => filter.field === "dates");
        if (!dateFilter) {
            throw new Error("Dates filter is required");
        }
        const queryBody = {
            ...OVERVIEW_DAILY_IMPRESSIONS_QUERY,
            filters: {
                "explore_overview.flt_date_range": parseDateFilter(dateFilter)
            }
        };
        const lookbackFilter = filters.where?.find((filter) => filter.field === "lookbackDays");
        if (lookbackFilter?.value !== undefined && lookbackFilter.value !== null) {
            queryBody.filters["explore_overview.pmt_lookback_window"] = String(lookbackFilter.value);
        }
        const mediaTypeFilter = filters.where?.find((filter) => filter.field === "mediaType");
        const mediaType = mediaTypeFilter?.value;
        if (mediaType && mediaType.length) {
            queryBody.filters["explore_overview.product_group"] = Array.isArray(mediaType)
                ? mediaType.join(",")
                : mediaType;
        }
        // Applied when present, matching the sibling breakdown and campaign-status
        // handlers. That is not a fail-open choice: these filters are what scope
        // the query to the partner, and the hook refuses to fetch without both, so
        // an empty selection never reaches here. The conditional only covers the
        // handler being called directly.
        const advertiserFilter = filters.where?.find((filter) => filter.field === "advertiserIds");
        const advertiserIds = advertiserFilter?.value;
        if (advertiserIds?.length) {
            queryBody.filters["advertisers.advertiser_id"] = advertiserIds.join(",");
        }
        const campaignFilter = filters.where?.find((filter) => filter.field === "campaignIds");
        const campaignIds = campaignFilter?.value;
        if (campaignIds?.length) {
            queryBody.filters["campaigns.campaign_id"] = campaignIds.join(",");
        }
        const hashKey = await this.sdk.looker.query.hashQuery(queryBody);
        return this.cache.promise(hashKey, () => this.sdk.looker.query
            .run(queryBody)
            .then(({ data, queryMeta }) => [
            {
                data: this.formatResponse(data),
                queryMeta
            }
        ])
            .catch((e) => {
            throw Object.assign(new Error("Failed to fetch overview daily impressions"), {
                cause: e
            });
        }));
    }
    /**
     * Pivots Looker's long-format rows (one per date per product) into one row
     * per date keyed by product, which is what a multi-series chart consumes.
     * Rows missing a date or product are dropped rather than bucketed under an
     * empty key, which would draw an unnamed series.
     */
    formatResponse(data) {
        const byDate = new Map();
        data.forEach((row) => {
            const date = row["explore_overview.date_date"];
            const product = row["explore_overview.product_group"];
            if (!date || !product) {
                return;
            }
            const products = byDate.get(date) ?? {};
            // Sum rather than assign: a product can appear more than once for a date
            // when the explore splits it further (device type, subtype).
            products[product] =
                (products[product] ?? 0) + (Number(row["explore_overview.total_impressions"]) || 0);
            byDate.set(date, products);
        });
        return [...byDate.entries()]
            .map(([date, byProduct]) => ({ date, byProduct }))
            .sort((a, b) => a.date.localeCompare(b.date));
    }
    async saveItem() {
        throw new NotImplementedError("saveItem");
    }
    async deleteItem() {
        throw new NotImplementedError("deleteItem");
    }
}
//# sourceMappingURL=DailyImpressions.js.map